// İçerik performansı ekranı (MOD-12 / K-3) — dashboard.read görüntüler.
//
// Tarih aralığı (RangePicker) + entityType filtresi ile top içerik: gruplu Bar +
// dağılım Pie + sayfalı DataTable (başlık → içerik düzenleme). BE streaming CSV
// export'u kimlik doğrulamalı blob fetch ile indirilir (FormSubmissions deseni).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, DatePicker, Select, Space, Tag, Typography } from 'antd';
import type { TableColumnType } from 'antd';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs, { type Dayjs } from 'dayjs';
import { PageHeader } from '@shared/components/PageHeader';
import { DataTable } from '@shared/components/DataTable';
import { ROUTES } from '@shared/constants/routes';
import { analyticsApi } from '@api/analytics';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { ANALYTICS_ENTITY_TYPES, type ContentPerformanceItem } from '../types';
import { ContentPerformanceCharts } from '../components/ContentPerformanceCharts';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DEFAULT_WINDOW_DAYS = 30;

// entityType → içerik düzenleme rotası. Bilinmeyen tür (ör. gelecekte eklenen)
// düz metin olarak kalır — kırık link üretmeyiz.
function editRouteFor(entityType: string, id: string): string | null {
  switch (entityType) {
    case 'article':
      return ROUTES.ARTICLE_EDIT.replace(':id', id);
    case 'page':
      return ROUTES.PAGE_EDIT.replace(':id', id);
    case 'campaign':
      return ROUTES.CAMPAIGN_EDIT.replace(':id', id);
    case 'branch':
      return ROUTES.BRANCH_EDIT.replace(':id', id);
    default:
      return null;
  }
}

function tabular(value: number) {
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value.toLocaleString('tr-TR')}</span>;
}

export function ContentPerformancePage() {
  const { t } = useTranslation(['analytics', 'common']);
  // Loose translator for dynamic entity-type keys (`entityType.<type>`); the
  // typed `t` rejects template-literal keys (DashboardPage `td` deseni).
  const td = t as unknown as (key: string) => string;
  const navigate = useNavigate();

  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().subtract(DEFAULT_WINDOW_DAYS - 1, 'day'),
    dayjs(),
  ]);
  const [entityType, setEntityType] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exporting, setExporting] = useState(false);

  const from = range[0].format('YYYY-MM-DD');
  const to = range[1].format('YYYY-MM-DD');

  const resetPage = () => setPage(1);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['content-performance', { from, to, entityType, page, pageSize }],
    queryFn: () => analyticsApi.contentPerformance({ from, to, entityType, page, pageSize }),
    staleTime: 60_000,
  });

  const items = useMemo(() => data?.items ?? [], [data?.items]);

  const labelForType = (type: string): string => {
    const mapped = td(`entityType.${type}`);
    return mapped === `entityType.${type}` ? type : mapped;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await analyticsApi.exportCsv({ from, to, entityType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `content-performance-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t('export.done'));
    } catch (err) {
      toast.error(resolveApiErrorMessage(err) || t('export.failed'));
    } finally {
      setExporting(false);
    }
  };

  const columns: TableColumnType<ContentPerformanceItem>[] = [
    {
      title: t('table.columns.title'),
      dataIndex: 'title',
      key: 'title',
      render: (title: string, r) => {
        const route = editRouteFor(r.entityType, r.entityId);
        const label = title || r.slug || t('table.untitled');
        return route ? (
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => void navigate(route)}>
            {label}
          </Button>
        ) : (
          <Text>{label}</Text>
        );
      },
    },
    {
      title: t('table.columns.type'),
      dataIndex: 'entityType',
      key: 'entityType',
      width: 130,
      render: (type: string) => <Tag>{labelForType(type)}</Tag>,
    },
    {
      title: t('table.columns.views'),
      dataIndex: 'views',
      key: 'views',
      width: 140,
      align: 'right',
      render: (v: number) => tabular(v),
    },
    {
      title: t('table.columns.clicks'),
      dataIndex: 'clicks',
      key: 'clicks',
      width: 140,
      align: 'right',
      render: (v: number) => tabular(v),
    },
  ];

  return (
    <div data-testid="content-performance-page">
      <PageHeader
        title={t('title')}
        description={t('description')}
        breadcrumb={[{ label: t('common:labels.home'), to: '/' }, { label: t('breadcrumb') }]}
        actions={
          <Button
            icon={<Download size={16} />}
            loading={exporting}
            onClick={() => void handleExport()}
            data-testid="content-perf-export"
          >
            {t('export.button')}
          </Button>
        }
      />

      {isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('error')}
          description={error ? resolveApiErrorMessage(error) : undefined}
          action={
            <Button size="small" danger onClick={() => void refetch()}>
              {t('retry')}
            </Button>
          }
        />
      ) : null}

      <ContentPerformanceCharts
        items={items}
        untitled={t('table.untitled')}
        labels={{
          topTitle: t('charts.topTitle'),
          distributionTitle: t('charts.distributionTitle'),
          views: t('charts.views'),
          clicks: t('charts.clicks'),
          empty: t('charts.empty'),
        }}
      />

      <DataTable<ContentPerformanceItem>
        data={items}
        columns={columns}
        rowKey={(r) => `${r.entityType}:${r.entityId}`}
        loading={isLoading}
        pagination={{
          page,
          size: pageSize,
          total: data?.total ?? 0,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        filters={
          <Space wrap>
            <RangePicker
              allowClear={false}
              value={range}
              format="DD.MM.YYYY"
              onChange={(v) => {
                if (v && v[0] && v[1]) {
                  setRange([v[0], v[1]]);
                  resetPage();
                }
              }}
            />
            <Select
              allowClear
              placeholder={t('filters.allTypes')}
              style={{ minWidth: 160 }}
              value={entityType}
              onChange={(v?: string) => {
                setEntityType(v);
                resetPage();
              }}
              options={ANALYTICS_ENTITY_TYPES.map((type) => ({
                value: type,
                label: labelForType(type),
              }))}
            />
          </Space>
        }
        emptyState={isLoading ? undefined : t('table.empty')}
      />
    </div>
  );
}

export default ContentPerformancePage;
